import { useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import DoneOutlineIcon from '@mui/icons-material/DoneOutline';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { DataGrid } from '@mui/x-data-grid';
import { post } from 'api';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import IconifyIcon from 'components/base/IconifyIcon';
import StyledTextField from 'components/styled/StyledTextField';

const TeamCorrectionTable = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
    search: '',
  });
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedLeaveId, setSelectedLeaveId] = useState(null);
  const [actionType, setActionType] = useState(null);
  const columns = useMemo(
    () => [
      {
        field: 'employee',
        headerName: 'Employee',
        minWidth: 200,
        renderCell: (params) => (
          <Typography>
            {params.row.user?.firstName} {params.row.user?.lastName}
          </Typography>
        ),
      },
      {
        field: 'date',
        headerName: 'Date',
        minWidth: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Typography variant="subtitle2">
            {moment(params.row.date).format('DD/MM/YYYY')}
          </Typography>
        ),
      },
      {
        field: 'attendanceId',
        headerName: 'Attendance Time',
        minWidth: 190,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Typography variant="subtitle2">
            {`${moment(params.row.attendance.loginTime).format('HH : mm A')}- ${moment(params.row.attendance.logoutTime).format('HH : mm A')}`}
          </Typography>
        ),
      },
      {
        field: 'requestedLogin',
        headerName: 'Requested Time',
        minWidth: 190,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Typography variant="subtitle2">
            {`${moment(params.row.requestedLogin).format('HH : mm A')}- ${moment(params.row.requestedLogout).format('HH : mm A')}`}
          </Typography>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 130,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const getColor = (status) => {
            switch (status) {
              case 'APPROVED':
                return 'success';
              case 'REJECTED':
                return 'error';
              case 'PENDING':
                return 'warning';
              default:
                return 'default';
            }
          };

          return (
            <Chip
              label={params.row.status}
              color={getColor(params.row.status)}
              size="small"
              sx={{ fontWeight: 500 }}
            />
          );
        },
      },
      {
        field: 'appliedOn',
        headerName: 'Update On',
        minWidth: 190,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Typography variant="subtitle2">
            {moment(params.row.updatedAt).format('DD/MM/YYYY HH:mm')}
          </Typography>
        ),
      },
      {
        field: 'action',
        headerName: 'Action',
        sortable: false,
        filterable: false,
        minWidth: 100,
        renderCell: (params) => {
          return (
            <Box>
              <IconButton
                disabled={params.row.status !== 'PENDING'}
                onClick={() => {
                  setSelectedLeaveId(params.row._id);
                  setActionType('approve');
                  setOpenConfirm(true);
                }}
              >
                <DoneOutlineIcon />
              </IconButton>
              <IconButton
                disabled={params.row.status !== 'PENDING'}
                onClick={() => {
                  setSelectedLeaveId(params.row._id);
                  setActionType('reject');
                  setOpenConfirm(true);
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          );
        },
      },
    ],
    [],
  );

  const handleConfirmAction = async () => {
    if (!selectedLeaveId) return;

    if (actionType === 'approve') {
      await approveLeave(selectedLeaveId, 'APPROVED');
    }

    if (actionType === 'reject') {
      await approveLeave(selectedLeaveId, 'REJECTED');
    }

    setOpenConfirm(false);
    setSelectedLeaveId(null);
    setActionType(null);
  };

  const approveLeave = async (id, status) => {
    try {
      const res = await post(`users/approveCorrection/${id}`, { status: status });

      if (res.status == 200) {
        enqueueSnackbar(res.message || 'Correction Approved', {
          variant: 'success',
          autoHideDuration: 2000,
        });
        fetchLeaves();
      } else {
        enqueueSnackbar(res.message || 'Error', {
          variant: 'error',
          autoHideDuration: 4000,
        });
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error', {
        variant: 'error',
        autoHideDuration: 4000,
      });
    } finally {
      setOpenConfirm(false);
      setSelectedLeaveId(null);
    }
  };

  const fetchLeaves = async () => {
    setLoading(true);
    try {
      const res = await post('users/teamCorrections', {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        search: paginationModel.search,
      });
      setRows(res.data.data);
      setTotal(res.data.total);
    } catch (err) {
      enqueueSnackbar(err?.message || 'Error fetching leaves', {
        variant: 'error',
        autoHideDuration: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPaginationModel((prev) => ({
        ...prev,
        search,
        page: 0,
      }));
    }, 600);

    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    fetchLeaves();
  }, [paginationModel]);

  return (
    <Paper sx={{ flex: 1, p: { xs: 3, md: 5 } }}>
      <StyledTextField
        type="search"
        fullWidth
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search Team Member"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <IconifyIcon icon="material-symbols:search-rounded" fontSize={20} />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          maxWidth: { sm: 200, md: 240 },
          mb: 3,
        }}
      />
      <DataGrid
        loading={loading}
        rows={rows}
        columns={columns}
        getRowId={(row) => row._id}
        rowCount={total}
        paginationModel={paginationModel}
        onPaginationModelChange={(model) =>
          setPaginationModel({
            page: model.page,
            pageSize: model.pageSize,
            search,
          })
        }
        pageSizeOptions={[5, 10, 20]}
      />
      <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
        <DialogTitle>
          {actionType === 'approve' ? 'Approve Correction' : 'Reject Correction'}
        </DialogTitle>

        <DialogContent>Are you sure you want to {actionType} this Correction?</DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenConfirm(false)}>Cancel</Button>

          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color={actionType === 'approve' ? 'success' : 'error'}
          >
            {actionType === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default TeamCorrectionTable;
